'use client';
import React from 'react';
import Icon from '@/components/ui/AppIcon';
import type { CallState } from './ActiveInteractionContent';

interface Props {
  duration: string;
  callState: CallState;
  onReturn: () => void;
  onEndCall: () => void;
}

export default function PersistentCallBar({ duration, callState, onReturn, onEndCall }: Props) {
  const isProcessing = callState === 'ai-processing';
  const isDraftReady = callState === 'ai-draft-ready';

  return (
    <div className={`flex items-center gap-2 sm:gap-3 text-white px-3 sm:px-4 py-2 flex-shrink-0 text-[12px] ${
      isProcessing ? 'bg-blue-800' : isDraftReady ? 'bg-purple-800' : 'bg-nav-bg'
    }`}>
      <div className="flex items-center gap-1.5 min-w-0">
        {isProcessing ? (
          <>
            <div className="flex gap-0.5 flex-shrink-0">
              {[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 bg-blue-300 rounded-full ai-listening-dot" style={{ animationDelay: `${i * 0.16}s` }} />)}
            </div>
            <span className="font-600 truncate">Ahmed Firdaus</span>
          </>
        ) : isDraftReady ? (
          <>
            <span className="text-[14px] flex-shrink-0">✨</span>
            <span className="font-600 truncate">Ahmed Firdaus</span>
          </>
        ) : (
          <>
            <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse flex-shrink-0" />
            <Icon name="PhoneIcon" size={13} className="flex-shrink-0" />
            <span className="font-600 truncate">Ahmed Firdaus</span>
          </>
        )}
      </div>
      <span className="font-tabular font-700 text-white/80 flex-shrink-0">{duration}</span>
      {isProcessing ? (
        <span className="bg-blue-500/30 text-blue-200 text-[10px] px-1.5 py-0.5 rounded font-600 flex-shrink-0">AI Processing</span>
      ) : isDraftReady ? (
        <span className="bg-purple-500/30 text-purple-200 text-[10px] px-1.5 py-0.5 rounded font-700 flex-shrink-0">Draft Ready</span>
      ) : (
        <span className="bg-red-500/20 text-red-300 text-[10px] px-1.5 py-0.5 rounded font-600 hidden sm:inline flex-shrink-0">ACTIVE</span>
      )}
      <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
        <button onClick={onReturn} className="btn-secondary px-2 sm:px-2.5 py-1 text-[11px] bg-white/10 border-white/20 text-white hover:bg-white/20 flex items-center gap-1">
          <Icon name="ArrowLeftIcon" size={11} />
          <span className="hidden sm:inline">{isDraftReady ? 'Review Draft' : 'Return to Call'}</span>
          <span className="sm:hidden">{isDraftReady ? 'Review' : 'Return'}</span>
        </button>
        {!isProcessing && !isDraftReady && (
          <>
            <button className="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors" title="Mute">
              <Icon name="MicrophoneIcon" size={13} />
            </button>
            <button className="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors hidden sm:flex" title="Hold">
              <Icon name="PauseIcon" size={13} />
            </button>
            <button onClick={onEndCall} className="p-1.5 bg-red-600 hover:bg-red-700 rounded-lg transition-colors" title="End Call">
              <Icon name="PhoneXMarkIcon" size={13} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}